import React, { useState, useEffect, useCallback } from 'react';
import {
	Alert,
	AutoComplete,
	Button,
	Collapse,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Select,
	message,
	Form,
} from 'antd';
import { CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import ReactGA from 'react-ga';
import Spinner from 'src/_shared/components/spinner/SpinnerComponent.jsx';
import { REFERRAL_STATUS, USER_ROLES } from 'src/_shared/constants/';
import uuid from 'uuid/v4';
import fileIcon from '../../_shared/assets/erin_lightgray.png';
import {
	downloadFromS3Signed,
	ml,
	parse,
	sanitize,
} from '../../_shared/services/utils.js';
import { BonusReportRecipientItem } from '../bonus-report-recipient-items';
import { EligibilityCustomItem } from '../eligibility-custom-value-items/EligibilityCustomItemsComponent.jsx';
import { EligibilityCustomValueObject } from '../eligibility-custom-value-items/EligibilityCustomValueObjectComponent.jsx';
import { RecipientItem } from '../recipient-items';
import { GroupCurrency } from './GroupCurrencyComponent.jsx';
import { GroupMeasurement } from './GroupMeasurementComponent.jsx';
import { TieredBonus } from './TieredBonusComponent.jsx';
import { BonusLabel, FlexContainer } from './referralTiersSettingsStyles.js';
import * as SettingStyle from './settingsStyles.js';
import { useSelector } from 'react-redux';

const { Option } = Select;

export const showConfirmation = ({
	title = 'Are you sure you want to update the Universal Referral Policy?',
	content = '',
	onConfirm,
	onCancel,
	okText = 'Confirm',
	cancelText = 'Cancel',
	width = 500,
}) => {
	Modal.confirm({
		title,
		icon: <QuestionCircleOutlined />,
		content,
		okText,
		cancelText,
		width,
		autoFocusButton: null,
		okType: 'primary',
		onOk: onConfirm,
		onCancel: onCancel,
	});
};

export const ReferralTiersSettings = (props) => {
	const {
		bonuses,
		company,
		location,
		onUpdateCompany,
		onAddTieredBonus,
		onUpdateTieredBonus,
		onUpdateUserGroup,
		userGroups,
		setCurrentUserGroup,
		updateCurrentUserCompany,
		allMultiLingualData,
	} = props;

	const currentUser = useSelector((state) => state.user?.currentUser);
	const companyId = company?.id || currentUser?.company?.id;

	// Early return if no company data is available
	if (!companyId) {
		return <Spinner />;
	}

	// Form instances
	const [mainForm] = Form.useForm();
	const [stagesForm] = Form.useForm();
	const [notificationForm] = Form.useForm();

	// State initialization
	const [state, setState] = useState({
		company: company || currentUser.company,
		isSuperAdmin: currentUser.role === USER_ROLES.SUPER_ADMIN,
		pendingInputValues: {},
		defaultActiveBonusKey: '',
		submitting: false,
		tieredBonuses: bonuses,
		theme: parse(get(company || currentUser.company, 'theme', '{}')),
		referralBonusNotificationRecipients: get(
			company || currentUser.company,
			'referralBonusNotificationRecipients',
			''
		),
		bonusReportSetting: parse(
			get(company || currentUser.company, 'bonusReportSetting', '{}')
		),
		automationSettings: parse(
			get(company || currentUser.company, 'automationSettings', '{}')
		),
		eligibilityChecks: parse(
			(company || currentUser.company)?.eligibiltyChecks
				? (company || currentUser.company).eligibiltyChecks
				: '{}'
		),
		ownershipSettings: parse(
			get(company || currentUser.company, 'ownershipSettings', '{}')
		),
		emailAddress: '',
		emailAddressLookupData: [],
		emailLookupChildren: [],
		recipientError: false,
		duplicateRecipientError: false,
		deliveryFrequency: [
			{ key: 'Weekly', val: 'Weekly' },
			{ key: 'Daily', val: 'Daily' },
			{ key: 'Monthly', val: 'Monthly' },
		],
		monthlyFrequency: Array.from({ length: 28 }, (_, i) => ({
			key: String(i + 1).padStart(2, '0'),
			val: i + 1,
		})),
		weeklyFrequency: [
			{ key: 'Monday', value: 1 },
			{ key: 'Tuesday', value: 2 },
			{ key: 'Wednesday', value: 3 },
			{ key: 'Thursday', value: 4 },
			{ key: 'Friday', value: 5 },
			{ key: 'Saturday', value: 6 },
			{ key: 'Sunday', value: 7 },
		],
		isEnableBonusReportSetting: false,
		referralBonusReportSettingrecipients: get(
			company || currentUser.company,
			'referralBonusReportSettingrecipients',
			''
		),
		selectedDeliveryFrequency: 'Weekly',
		selectedScheduleTime: '',
		selectedDeliveryDateOption: [
			'Monday',
			'Tuesday',
			'Wednesday',
			'Thursday',
			'Friday',
			'Saturday',
		],
		bonusEmailAddress: '',
		customFieldNameValue: {},
		customFieldObjectInvalidOrValidValue: {},
		selectedCustom: null,
		stages: [],
		errorImageSrc: null,
	});

	// Update state helper
	const updateState = useCallback((updates) => {
		setState((prevState) => ({ ...prevState, ...updates }));
	}, []);

	// Initialize component
	useEffect(() => {
		const initializeComponent = async () => {
			const { state: locationState } = location;
			if (locationState && locationState.openModel) {
				handleAddNewBonusClick();
			}

			if (locationState && locationState.bonusReportSetting == null) {
				updateState({
					bonusReportSetting: {
						isEnable: false,
						frequency: '',
						time: 1,
						emailAddresses: '',
					},
				});
			}

			const stages = get(currentUser, 'company.stages');
			if (stages != null && stages.length > 0) {
				updateState({ stages: JSON.parse(stages) });
			}

			// Google Analytics initialization
			const host = window.location.hostname;
			if (host === 'referrals.aus.com') {
				ReactGA.initialize('UA-128630809-2');
				ReactGA.pageview(window.location.pathname + window.location.search);
			} else if (host === 'app.erinapp.com') {
				ReactGA.initialize('UA-128630809-3');
				ReactGA.pageview(window.location.pathname + window.location.search);
			}

			// Load error image
			const errorImage = get(state.company, 'errorImage.key', false);
			if (errorImage) {
				const errorImageSource = await downloadFromS3Signed(
					errorImage,
					'erin-images'
				);
				updateState({ errorImageSrc: errorImageSource });
			}
		};

		initializeComponent();
	}, []);

	// Update state when props change
	useEffect(() => {
		if (bonuses !== state.tieredBonuses) {
			updateState({ tieredBonuses: bonuses });
		}
	}, [bonuses]);

	useEffect(() => {
		if (company !== state.company) {
			const parsedBonusReportSetting = parse(
				get(company, 'bonusReportSetting', '{}')
			);

			updateState({
				company,
				referralBonusNotificationRecipients: get(
					company,
					'referralBonusNotificationRecipients',
					''
				),
				bonusReportSetting: parsedBonusReportSetting,
				automationSettings: parse(get(company, 'automationSettings', '{}')),
				eligibilityChecks: parse(
					company?.eligibiltyChecks ? company.eligibiltyChecks : '{}'
				),
				ownershipSettings: parse(
					company?.ownershipSettings ? company.ownershipSettings : '{}'
				),
			});
		}
	}, [company, state.company]);

	// Debounced handlers
	const handleOwnershipSettingsDebouncedConfirm = useCallback(
		debounce((value) => {
			showConfirmation({
				onConfirm: () => {
					let ownershipSettings = { ...get(state, 'ownershipSettings', {}) };
					ownershipSettings.days = value;

					updateState({
						ownershipSettings,
						pendingInputValues: {
							...state.pendingInputValues,
							ownershipDays: undefined,
						},
					});

					onUpdateCompany({
						input: {
							id: state.company?.id || companyId,
							ownershipSettings: JSON.stringify(ownershipSettings),
						},
					});
				},
				onCancel: () => {
					updateState({
						pendingInputValues: {
							...state.pendingInputValues,
							ownershipDays: undefined,
						},
					});
				},
			});
		}, 1000),
		[state]
	);

	const debouncedEligibilityConfirmation = useCallback(
		debounce((id, value) => {
			showConfirmation({
				onConfirm: () => {
					const updatedEligibilityChecks = { ...state.eligibilityChecks };
					updatedEligibilityChecks[id] = value.trim();

					updateState({
						eligibilityChecks: updatedEligibilityChecks,
						pendingInputValues: {
							...state.pendingInputValues,
							[`eligibility-${id}`]: undefined,
						},
					});

					onUpdateCompany({
						input: {
							id: state.company?.id || companyId,
							eligibiltyChecks: JSON.stringify(updatedEligibilityChecks),
						},
					});
				},
				onCancel: () => {
					updateState({
						pendingInputValues: {
							...state.pendingInputValues,
							[`eligibility-${id}`]: undefined,
						},
					});
				},
			});
		}, 1000),
		[state]
	);

	// Event handlers
	const handleAddNewBonusClick = useCallback(() => {
		const { tieredBonuses } = state;
		const index = tieredBonuses.findIndex((x) => x.id.length < 32);
		if (index >= 0) return;

		const newId = 'new' + Date.now();
		const newBonus = {
			id: newId,
			companyId,
			name: '',
			tiers: [],
		};

		if (tieredBonuses) {
			updateState({
				defaultActiveBonusKey: newId,
				tieredBonuses: [newBonus, ...tieredBonuses],
			});
		} else {
			updateState({
				defaultActiveBonusKey: newId,
				tieredBonuses: [newBonus],
			});
		}
	}, [state.tieredBonuses, companyId]);

	const handleDeleteTieredBonus = useCallback(
		(bonusId) => {
			updateState({ submitting: true });
			const { tieredBonuses } = state;

			const copy = [...tieredBonuses];
			const index = copy.findIndex((x) => x.id.length < 32);
			if (index >= 0) copy.splice(index, 1);
			const index_ = copy.findIndex((x) => x.id === bonusId);

			if (index_ >= 0) {
				copy.splice(index_, 1);
				const updatedBonus = { ...tieredBonuses[index_], archived: true };

				onUpdateTieredBonus(updatedBonus).then(() => {
					updateState({
						tieredBonuses: copy,
						defaultActiveBonusKey: undefined,
						submitting: false,
					});
					message.success('Bonus deleted');
				});
			} else {
				updateState({
					tieredBonuses: copy,
					defaultActiveBonusKey: undefined,
					submitting: false,
				});
			}
		},
		[state.tieredBonuses, onUpdateTieredBonus]
	);

	const handleEmailChange = useCallback(
		(value) => {
			value &&= sanitize(value);
			const recipient = state.referralBonusNotificationRecipients
				? state.referralBonusNotificationRecipients.split(',')
				: [];

			const companyDefaultEmail = recipient[0]
				? recipient[0].slice(recipient[0].indexOf('@'), recipient[0].length)
				: '';

			const emailAddressList =
				!value || value.includes('@')
					? []
					: [
							`${value}@gmail.com`,
							`${value}@hotmail.com`,
							`${value}@outlook.com`,
							`${value}@yahoo.com`,
						];

			if (
				!emailAddressList.includes(`${value}${companyDefaultEmail}`) &&
				emailAddressList.length > 0
			) {
				emailAddressList.unshift(`${value}${companyDefaultEmail}`);
			}

			const children = state.emailAddressLookupData.map((email) => (
				<AutoComplete.Option key={email}>{email}</AutoComplete.Option>
			));

			updateState({
				emailAddressLookupData: emailAddressList,
				emailLookupChildren: children,
				emailAddress: value.toLowerCase(),
			});
		},
		[state.referralBonusNotificationRecipients, state.emailAddressLookupData]
	);

	const handleAutomationSettings = useCallback(
		(value, setting) => {
			const originalSettings = { ...state.automationSettings };

			showConfirmation({
				onConfirm: () => {
					const newSettings = { ...state.automationSettings, [setting]: value };
					updateState({ automationSettings: newSettings });

					onUpdateCompany({
						input: {
							id: state.company?.id || companyId,
							automationSettings: JSON.stringify(newSettings),
						},
					});
				},
				onCancel: () => {
					updateState({ automationSettings: originalSettings });
				},
			});
		},
		[state.automationSettings, state.company?.id, onUpdateCompany]
	);

	const handleBonusFrequency = useCallback(
		(value) => {
			const bonusReportSetting = { ...state.bonusReportSetting };
			bonusReportSetting.frequency = value;
			bonusReportSetting.time = 1;

			updateState({ bonusReportSetting });

			onUpdateCompany({
				input: {
					id: state.company?.id || companyId,
					bonusReportSetting: JSON.stringify(bonusReportSetting),
				},
			});

			const selectedDeliveryDateOption =
				value === 'Weekly' || value === 'Monthly';
			updateState({ selectedDeliveryDateOption });
		},
		[state.bonusReportSetting, state.company?.id, onUpdateCompany]
	);

	const handleOwnershipSettings = useCallback(
		(value, type) => {
			const originalSettings = { ...get(state, 'ownershipSettings', {}) };

			showConfirmation({
				onConfirm: () => {
					let ownershipSettings = get(state, 'ownershipSettings', {}) || {};

					switch (type) {
						case 'enabled':
							ownershipSettings.enabled = value;
							break;
						case 'rollingOwnershipDates':
							ownershipSettings.rollingOwnershipDates = value.target
								? value.target.checked
								: value;
							break;
					}

					updateState({ ownershipSettings });

					onUpdateCompany({
						input: {
							id: state.company?.id || companyId,
							ownershipSettings: JSON.stringify(ownershipSettings),
						},
					});
				},
				onCancel: () => {
					updateState({ ownershipSettings: originalSettings });
				},
			});
		},
		[state, onUpdateCompany]
	);

	const handleNewEligibilityCustomValidator = useCallback(
		(event) => {
			let value = event.target.value ? event.target.value : '';
			const name = event.target.id ? event.target.id : '';
			const type = event.target.name ? event.target.name : '';
			value &&= sanitize(value);
			updateState({
				customFieldNameValue: {
					...state.customFieldNameValue,
					name,
					value,
					type,
				},
			});
		},
		[state.customFieldNameValue]
	);

	const handleNewEligibilityCustomValueVluesValidator = useCallback(
		(event) => {
			let value = event.target.value ? event.target.value : '';
			const name = event.target.id ? event.target.id : '';
			const type = event.target.name ? event.target.name : '';
			value &&= sanitize(value);
			updateState({
				customFieldObjectInvalidOrValidValue: {
					...state.customFieldObjectInvalidOrValidValue,
					name,
					value,
					type,
				},
			});
		},
		[state.customFieldObjectInvalidOrValidValue]
	);

	const addCustomValueFieldName = useCallback(() => {
		const originalEligibilityChecks = { ...state.eligibilityChecks };
		const originalCustomFieldNameValue = { ...state.customFieldNameValue };

		const { eligibilityChecks, customFieldNameValue } = state;
		const fieldName = customFieldNameValue.name;
		const fieldValue = customFieldNameValue.value;
		const fieldType = customFieldNameValue.type;

		showConfirmation({
			onConfirm: () => {
				if (fieldType === 'object') {
					const customValuesToCheck =
						eligibilityChecks && eligibilityChecks.CustomValuesToCheck
							? eligibilityChecks.CustomValuesToCheck
							: [];

					if (
						fieldValue &&
						!customValuesToCheck.find((element) => element.name === fieldValue)
					) {
						const newElement = { name: fieldValue };
						customValuesToCheck.push(newElement);
						const updatedChecks = {
							...eligibilityChecks,
							CustomValuesToCheck: customValuesToCheck,
						};

						updateState({
							eligibilityChecks: updatedChecks,
							customFieldNameValue: {},
						});

						onUpdateCompany({
							input: {
								id: state.company.id,
								eligibiltyChecks: JSON.stringify(updatedChecks),
							},
						});
					}
				} else if (fieldType === 'string') {
					const customValuesToCheck =
						eligibilityChecks && eligibilityChecks[fieldName]
							? eligibilityChecks[fieldName].split('|')
							: [];

					if (
						customFieldNameValue &&
						customValuesToCheck.indexOf(fieldValue) === -1
					) {
						customValuesToCheck.push(fieldValue);
						const updatedChecks = {
							...eligibilityChecks,
							[fieldName]: customValuesToCheck.join('|'),
						};

						updateState({
							eligibilityChecks: updatedChecks,
							customFieldNameValue: {},
						});

						onUpdateCompany({
							input: {
								id: state.company.id,
								eligibiltyChecks: JSON.stringify(updatedChecks),
							},
						});
					}
				}
			},
			onCancel: () => {
				updateState({
					eligibilityChecks: originalEligibilityChecks,
					customFieldNameValue: originalCustomFieldNameValue,
				});
			},
		});
	}, [state, onUpdateCompany]);

	const handleFilterCustomValue = useCallback(
		(name) => {
			const { eligibilityChecks } = state;
			const customValue = eligibilityChecks.CustomValuesToCheck?.find((x) => {
				return x.name === name;
			});
			updateState({ selectedCustom: customValue });
		},
		[state.eligibilityChecks]
	);

	const handleCreateCustomValueValid = useCallback(() => {
		const originalEligibilityChecks = { ...state.eligibilityChecks };
		const originalSelectedCustom = state.selectedCustom
			? { ...state.selectedCustom }
			: null;
		const originalCustomFieldObjectInvalidOrValidValue = {
			...state.customFieldObjectInvalidOrValidValue,
		};

		const {
			eligibilityChecks,
			selectedCustom,
			customFieldObjectInvalidOrValidValue,
		} = state;
		const newValid = customFieldObjectInvalidOrValidValue.value;

		showConfirmation({
			onConfirm: () => {
				if (selectedCustom.validValues) {
					const currentValids = selectedCustom.validValues.split('|');
					if (
						newValid &&
						!currentValids.includes(newValid) &&
						newValid.trim() !== ''
					) {
						currentValids.push(newValid);
					}
					selectedCustom.validValues = currentValids.join('|');
				} else if (newValid && newValid.trim() !== '') {
					selectedCustom.validValues = newValid;
				}

				const updatedChecks = { ...eligibilityChecks };
				const customToCheck = updatedChecks.CustomValuesToCheck?.find(
					(f) => f.name === selectedCustom.name
				);
				if (customToCheck) {
					customToCheck.validValues = selectedCustom.validValues;
				}

				updateState({
					eligibilityChecks: updatedChecks,
					customFieldObjectInvalidOrValidValue: {},
					selectedCustom,
				});

				onUpdateCompany({
					input: {
						id: state.company.id,
						eligibiltyChecks: JSON.stringify(updatedChecks),
					},
				});
			},
			onCancel: () => {
				updateState({
					eligibilityChecks: originalEligibilityChecks,
					selectedCustom: originalSelectedCustom,
					customFieldObjectInvalidOrValidValue:
						originalCustomFieldObjectInvalidOrValidValue,
				});
			},
		});
	}, [state, onUpdateCompany]);

	const handleCreateCustomValueInvalid = useCallback(() => {
		const originalEligibilityChecks = { ...state.eligibilityChecks };
		const originalSelectedCustom = state.selectedCustom
			? { ...state.selectedCustom }
			: null;
		const originalCustomFieldObjectInvalidOrValidValue = {
			...state.customFieldObjectInvalidOrValidValue,
		};

		const {
			eligibilityChecks,
			selectedCustom,
			customFieldObjectInvalidOrValidValue,
		} = state;
		const newInvalid = customFieldObjectInvalidOrValidValue.value;

		showConfirmation({
			onConfirm: () => {
				if (selectedCustom.invalidValues) {
					const currentInvalids = selectedCustom.invalidValues.split('|');
					if (
						newInvalid &&
						!currentInvalids.includes(newInvalid) &&
						newInvalid.trim() !== ''
					) {
						currentInvalids.push(newInvalid);
					}
					selectedCustom.invalidValues = currentInvalids.join('|');
				} else if (newInvalid && newInvalid.trim() !== '') {
					selectedCustom.invalidValues = newInvalid;
				}

				const updatedChecks = { ...eligibilityChecks };
				const customToCheck = updatedChecks.CustomValuesToCheck?.find(
					(f) => f.name === selectedCustom.name
				);
				if (customToCheck) {
					customToCheck.invalidValues = selectedCustom.invalidValues;
				}

				updateState({
					eligibilityChecks: updatedChecks,
					customFieldObjectInvalidOrValidValue: {},
					selectedCustom,
				});

				onUpdateCompany({
					input: {
						id: state.company.id,
						eligibiltyChecks: JSON.stringify(updatedChecks),
					},
				});
			},
			onCancel: () => {
				updateState({
					eligibilityChecks: originalEligibilityChecks,
					selectedCustom: originalSelectedCustom,
					customFieldObjectInvalidOrValidValue:
						originalCustomFieldObjectInvalidOrValidValue,
				});
			},
		});
	}, [state, onUpdateCompany]);

	const validateEmail = useCallback((email) => {
		const re = /\S+@\S+\.\S+/;
		return re.test(email);
	}, []);

	const handleSubmit = useCallback(async () => {
		try {
			const values = await mainForm.validateFields();
			if (state.submitting) return;

			const bonusMap = new Map();
			const fields = Object.keys(values);
			const newId = uuid();
			updateState({ newId });

			// Process bonus fields
			const handleBonus = (field) => {
				const token = 'tieredBonus-';
				if (!field.startsWith(token)) return;

				const bonusKey = field.slice(token.length);
				let name = values[field];
				name &&= sanitize(name);
				bonusMap.set(bonusKey, {
					id: newId,
					name,
					companyId,
					...(bonusKey.startsWith('new') ? {} : { id: bonusKey }),
				});
			};

			// Process tier fields
			const getExclusions = (tField) => {
				const filteredFields = [];
				const trim = tField.split('-');
				const targetField = trim.slice(0, -2).join('-');
				for (const f of fields) {
					if (
						targetField !== '' &&
						f.startsWith(targetField) &&
						f.endsWith('-exclusions')
					)
						filteredFields.push(f);
				}

				const results = [];
				for (const field of filteredFields) {
					const tierKeyBase = field.slice(5, 41);
					const exclusionsFromState = state?.[tierKeyBase]?.exclusions;
					if (results.length === 0 && exclusionsFromState) {
						results.push(...exclusionsFromState);
					} else if (results.length === 0) {
						results.push(...values[field]);
					}
				}
				return results;
			};

			const getInclusions = (tField) => {
				const filteredFields = [];
				const trim = tField.split('-');
				const targetField = trim.slice(0, -2).join('-');
				for (const f of fields) {
					if (
						targetField !== '' &&
						f.startsWith(targetField) &&
						f.endsWith('-inclusions')
					)
						filteredFields.push(f);
				}

				const results = [];
				for (const field of filteredFields) {
					const tierKeyBase = field.slice(5, 41);
					const inclusionsFromState = state?.[tierKeyBase]?.inclusions;
					if (results.length === 0 && inclusionsFromState) {
						results.push(...inclusionsFromState);
					} else if (results.length === 0) {
						results.push(...values[field]);
					}
				}
				return results;
			};

			const handleTier = (field, exclusions, inclusions) => {
				const token = 'tier-';
				const skipTokens = [
					'-payoutdays',
					'-recipienttype',
					'-usergroup',
					'-diversityincentive',
					'-exclusions',
					'-exclusionscategory',
					'-excludepicker',
					'-inclusions',
					'-inclusionscategory',
					'-includepicker',
				];

				const fieldsToSkip =
					!field.startsWith(token) ||
					skipTokens.some((skipToken) => field.endsWith(skipToken));

				if (fieldsToSkip) return;

				const end = field.lastIndexOf('-');
				const bonusKey = field.substring(token.length, end);
				const bonus = bonusMap.get(bonusKey);

				const payOutDaysKey = fields.find(
					(f) => f.startsWith(field) && f.endsWith('-payoutdays')
				);
				const recipientTypeKey = fields.find(
					(f) => f.startsWith(field) && f.endsWith('-recipienttype')
				);
				const userGroupKey = fields.find(
					(f) => f.startsWith(field) && f.endsWith('-usergroup')
				);
				const diversityGroupKey = fields.find(
					(f) => f.startsWith(field) && f.endsWith('-diversityincentive')
				);

				const userGroup = values[userGroupKey];

				const tierValues = {
					amount: values[field],
					payOutDays: values[payOutDaysKey],
					recipientType: values[recipientTypeKey],
					userGroup,
					rules: {},
				};

				if (diversityGroupKey)
					tierValues.rules.diversityHire = {
						active: values[diversityGroupKey] >= 0,
						amount: values[diversityGroupKey],
					};
				if (exclusions) {
					tierValues.rules.exclusions = exclusions;
				}
				if (inclusions) {
					tierValues.rules.inclusions = inclusions;
				}

				const tier = JSON.stringify(tierValues);
				if (bonus.tiers) {
					bonus.tiers.push(tier);
				} else {
					bonus.tiers = [tier];
				}
			};

			// Process all fields
			for (const key of fields) {
				const exclusions = getExclusions(key);
				const inclusions = getInclusions(key);
				handleBonus(key);
				handleTier(key, exclusions, inclusions);
			}

			// Save bonuses
			const addPromises = [...bonusMap].map(async (bonus) => {
				bonus = bonus[1];
				if (bonus.id && bonus.id.startsWith('new')) {
					delete bonus.id;
				}

				// Add required fields for GraphQL cache
				bonus.archived = false;

				if (bonus.id) {
					return onUpdateTieredBonus(bonus);
				}
				return onAddTieredBonus(bonus);
			});

			const resp = await Promise.all(addPromises);

			// Handle response structure - could be updateTieredBonus or addTieredBonus
			let bonus;
			if (resp[0]?.data?.updateTieredBonus) {
				bonus = resp[0].data.updateTieredBonus;
			} else if (resp[0]?.data?.addTieredBonus) {
				bonus = resp[0].data.addTieredBonus;
			} else if (resp[0]?.data) {
				// Check if data contains the bonus directly
				bonus = resp[0].data;
			} else {
				// Fallback - use the response directly if it's the bonus object
				bonus = resp[0];
			}

			if (bonus && bonus.id) {
				const copy = [...state.tieredBonuses];
				const index = copy.findIndex((x) => x.id.length < 32);
				if (index >= 0) copy.splice(index, 1);
				const duplicateIndex = copy.findIndex((x) => x.id === bonus.id);
				if (duplicateIndex >= 0) copy.splice(duplicateIndex, 1);

				const newBonuses = [bonus, ...copy];
				updateState({ tieredBonuses: newBonuses, submitting: false });
				message.success('Your data was saved');
			} else {
				updateState({ submitting: false });
				message.error('Failed to save bonus data');
			}
		} catch (error) {
			updateState({ submitting: false });
			console.error('Form validation failed:', error);
		}
	}, [mainForm, state, onUpdateTieredBonus, onAddTieredBonus, companyId]);

	// Form submission handlers
	const handleSubmitClick = useCallback(
		(e) => {
			e.preventDefault();
			updateState({ submitting: true });
			handleSubmit().finally(() => {
				updateState({ submitting: false });
			});
		},
		[handleSubmit]
	);

	const handleNotificationSubmit = useCallback(
		async (values) => {
			const { emailAddress } = state;
			const isDuplicateRecipient = state.referralBonusNotificationRecipients
				? state.referralBonusNotificationRecipients.includes(emailAddress)
				: false;

			if (emailAddress && !isDuplicateRecipient) {
				let recipients = state.referralBonusNotificationRecipients
					? state.referralBonusNotificationRecipients.split(',')
					: [];

				if (!recipients.includes(emailAddress) && validateEmail(emailAddress)) {
					recipients.push(emailAddress);
				}

				const spaceIndex = recipients.indexOf(' ');
				if (spaceIndex > -1) {
					recipients.splice(spaceIndex, 1);
				}

				recipients = recipients.join(',');
				if (recipients === '') {
					recipients = ' ';
				}

				updateState({
					referralBonusNotificationRecipients: recipients,
					emailAddress: '',
				});

				await onUpdateCompany({
					input: {
						id: state.company?.id || companyId,
						referralBonusNotificationRecipients: recipients,
					},
				});

				handleEmailChange('');
			} else if (emailAddress && isDuplicateRecipient) {
				updateState({ duplicateRecipientError: true });
				return;
			}

			notificationForm.resetFields();
		},
		[state, onUpdateCompany, validateEmail, handleEmailChange, notificationForm]
	);

	const handleSubmitStages = useCallback(
		async (values) => {
			const { stages } = state;
			const existingStages = [];

			let referralStatus = values.referralMiddleStage;
			referralStatus &&= sanitize(referralStatus);

			let stageData = {};
			for (let i = 1; i <= stages.length; i++) {
				let value = values[`stage-${i}`];
				value &&= sanitize(value);

				stageData = { [`stage${i}`]: value };
				existingStages.push(stageData);
			}

			const referralCustomStatuses = { ...REFERRAL_STATUS };
			const currentMapping = get(company, 'referralCustomStatuses')
				? {
						...REFERRAL_STATUS,
						...parse(get(company, 'referralCustomStatuses')),
					}
				: { ...REFERRAL_STATUS };

			const referralCustomStatusesMap = {
				...referralCustomStatuses,
				...currentMapping,
			};

			Object.entries(referralCustomStatusesMap).forEach(([key]) => {
				referralCustomStatuses[key] = values[key];
			});

			updateState({ stages: existingStages });

			const updatedCompany = await onUpdateCompany({
				input: {
					id: state.company?.id || companyId,
					referralStatus,
					referralCustomStatuses: JSON.stringify(referralCustomStatuses),
					stages: JSON.stringify(existingStages),
				},
			}).then((response) => response.data.updateCompany);

			updateCurrentUserCompany(updatedCompany);
			message.success('Stages saved successfully.', 5);
			stagesForm.resetFields();
		},
		[state, company, onUpdateCompany, updateCurrentUserCompany, stagesForm]
	);

	// Render methods
	const renderBonusNotification = () => {
		const { duplicateRecipientError } = state;

		return (
			<div className="bs-form">
				<h6 className="bs-form-label">Individual Referral Eligible Alerts</h6>
				<Form form={notificationForm} onFinish={handleNotificationSubmit}>
					<div className="bs-form-grp">
						<div className="custom-form-group">
							<label className="bs-label">
								Send bonus eligible notifications to:
							</label>
							<Form.Item
								name="recipientAddress"
								rules={[
									{
										type: 'email',
										message: 'The input is not a valid email address!',
									},
								]}
							>
								<AutoComplete
									options={state.emailAddressLookupData.map((email) => ({
										value: email,
									}))}
									onChange={handleEmailChange}
									onSelect={handleEmailChange}
									className="custom-input"
									placeholder="Enter Email "
								/>
							</Form.Item>
						</div>

						<Button
							type="link"
							className="add-btn"
							size="small"
							htmlType="submit"
						>
							<span className="icon-circle">
								<i className="icon-plus" />
							</span>
							Add Email
						</Button>
					</div>
				</Form>

				{duplicateRecipientError && (
					<Alert message="This email address already exists." type="error" />
				)}

				<h6 className="bs-form-label">View or Delete Email Recipients</h6>
				<RecipientItem
					company={state.company}
					referralBonusNotificationRecipients={
						state.referralBonusNotificationRecipients
					}
					handleRecipientError={() => updateState({ recipientError: true })}
					updateRecipientListState={(newRecipients) =>
						updateState({ referralBonusNotificationRecipients: newRecipients })
					}
					onUpdateCompany={onUpdateCompany}
				/>
			</div>
		);
	};

	const renderList = () => {
		const { tieredBonuses, defaultActiveBonusKey, errorImageSrc } = state;
		const whiteLabel = get(state.company, 'whiteLabel');

		if (tieredBonuses.length === 0) {
			return (
				<div className="no-content">
					{whiteLabel ? (
						<img
							className="no-content-icon"
							src={errorImageSrc}
							alt="error image"
						/>
					) : (
						<img alt="erin-logo" className="no-content-icon" src={fileIcon} />
					)}
					<p className="no-content-text">No bonuses have been created. </p>
				</div>
			);
		}

		if (tieredBonuses && tieredBonuses.length > 0 && defaultActiveBonusKey) {
			return (
				<Collapse
					bordered={false}
					activeKey={defaultActiveBonusKey}
					className="bonus-collapse"
					expandIconPosition="end"
				>
					{renderTieredBonuses()}
				</Collapse>
			);
		}

		if (tieredBonuses && tieredBonuses.length > 0 && !defaultActiveBonusKey) {
			return (
				<Collapse
					bordered={false}
					className="bonus-collapse"
					expandIconPosition="end"
				>
					{renderTieredBonuses()}
				</Collapse>
			);
		}
	};

	const renderTieredBonuses = () => {
		const { tieredBonuses, theme, submitting } = state;
		if (!tieredBonuses) return;

		const sortedTieredBonuses = tieredBonuses.sort((a, b) => {
			const aName = a.name.toUpperCase();
			const bName = b.name.toUpperCase();
			if (aName < bName) return -1;
			if (aName > bName) return 1;
			return 0;
		});

		return sortedTieredBonuses.map((bonus) => {
			const { tiers } = bonus;
			if (!tiers) return null;

			return (
				<Collapse.Panel
					key={bonus.id}
					extra={
						<div className="bonus-collapse-icon-wrap">
							<i className="icon-arrow-down bonus-collapse-icon" />
						</div>
					}
					className="bonus-collapse-card"
					header={<h4>{bonus.name}</h4>}
					onChange={() => updateState({ defaultActiveBonusKey: bonus.id })}
				>
					<div className="bonus-rule-actions">
						<Button type="primary" onClick={handleSubmitClick}>
							Save Bonus
						</Button>

						{bonuses.some((storedBonus) => storedBonus.id === bonus.id) && (
							<Popconfirm
								title="Duplicate Bonus?"
								placement="bottom"
								okText="Yes"
								cancelText="No"
								onConfirm={() => {
									const duplicateBonus = { ...bonus };
									duplicateBonus.id = uuid();
									duplicateBonus.name = `${duplicateBonus.name} (Copy)`;

									return onUpdateTieredBonus(duplicateBonus).then(() =>
										updateState({
											tieredBonuses: [...tieredBonuses, duplicateBonus],
										})
									);
								}}
							>
								<Button ghost type="primary">
									<CopyOutlined />
									Duplicate Bonus
								</Button>
							</Popconfirm>
						)}

						<Popconfirm
							title="Delete Bonus?"
							placement="bottom"
							okText="Yes"
							cancelText="No"
							onConfirm={() => handleDeleteTieredBonus(bonus.id)}
						>
							<Button ghost className="ant-btn-danger">
								<i className="icon-bin" />
								{ml('Delete Bonus', currentUser, allMultiLingualData)}
							</Button>
						</Popconfirm>
					</div>

					<TieredBonus
						currentUser={currentUser}
						allMultiLingualData={allMultiLingualData}
						key={`tiered-bonus-${bonus.id}`}
						bonusId={bonus.id}
						companyId={bonus.companyId}
						name={bonus.name}
						tiers={bonus.tiers}
						submitting={submitting}
						userGroups={userGroups}
						updateExclusions={(exclusions, tierKeyBase) => {
							const inclusions = state?.[tierKeyBase]?.inclusions || [];
							updateState({
								exclusions,
								[tierKeyBase]: { exclusions, inclusions },
							});
						}}
						updateInclusions={(inclusions, tierKeyBase) => {
							const exclusions = state?.[tierKeyBase]?.exclusions || [];
							updateState({
								inclusions,
								[tierKeyBase]: { inclusions, exclusions },
							});
						}}
						theme={theme}
						onChange={() => updateState({ defaultActiveBonusKey: undefined })}
						onDelete={() => handleDeleteTieredBonus(bonus.id)}
						form={mainForm}
					/>
				</Collapse.Panel>
			);
		});
	};

	const renderEligibilitySettings = () => {
		const {
			isSuperAdmin,
			eligibilityChecks,
			automationSettings,
			ownershipSettings,
			pendingInputValues,
			selectedCustom,
		} = state;

		const handleEligibilityCheckOnChange = useCallback(
			(value) => {
				const originalChecks = { ...eligibilityChecks };

				showConfirmation({
					onConfirm: () => {
						let newValue;
						if (value.target.type === 'checkbox') {
							newValue = !eligibilityChecks[value.target.id];
						}

						if (
							value.target.type === 'text' &&
							value.target.value &&
							value.target.value.trim() !== ''
						) {
							newValue = value.target.value.trim();
						}

						if (newValue != null) {
							const updatedChecks = {
								...eligibilityChecks,
								[value.target.id]: newValue,
							};
							updateState({ eligibilityChecks: updatedChecks });

							onUpdateCompany({
								input: {
									id: state?.company?.id || companyId,
									eligibiltyChecks: JSON.stringify(updatedChecks),
								},
							});
						}
					},
					onCancel: () => {
						updateState({ eligibilityChecks: originalChecks });
					},
				});
			},
			[eligibilityChecks, state?.company?.id, onUpdateCompany]
		);

		const handleEligibilityInputChange = useCallback(
			(event) => {
				const inputId = event.target.id;
				const inputValue = event.target.value;

				updateState({
					pendingInputValues: {
						...pendingInputValues,
						[`eligibility-${inputId}`]: inputValue,
					},
				});

				debouncedEligibilityConfirmation(inputId, inputValue);
			},
			[pendingInputValues, debouncedEligibilityConfirmation]
		);

		const handleEligibilityGranularityCheckOnChange = useCallback(
			(event) => {
				const { id, checked, type } = event.target;
				const originalEligibilityChecks = { ...eligibilityChecks };

				showConfirmation({
					onConfirm: () => {
						if (type === 'checkbox') {
							const typeValues = id.split('-');
							const field = typeValues[0];
							const granularity = typeValues[1];

							let currentValues = eligibilityChecks[field]
								? eligibilityChecks[field].split('|')
								: [];

							if (checked) {
								if (!currentValues.includes(granularity)) {
									currentValues.push(granularity);
								}
							} else {
								currentValues = currentValues.filter((v) => v !== granularity);
							}

							const updatedChecks = {
								...eligibilityChecks,
								[field]: currentValues.join('|'),
							};
							updateState({ eligibilityChecks: updatedChecks });

							onUpdateCompany({
								input: {
									id: state?.company?.id || companyId,
									eligibiltyChecks: JSON.stringify(updatedChecks),
								},
							});
						}
					},
					onCancel: () => {
						updateState({ eligibilityChecks: originalEligibilityChecks });
					},
				});
			},
			[eligibilityChecks, state?.company?.id, onUpdateCompany]
		);

		return (
			<div>
				<div className="bs-form" style={isSuperAdmin ? {} : { opacity: 0.6 }}>
					<div style={{ marginBottom: 10 }}>
						<em>
							{ml(
								'Note: these automations do not apply to manually created bonuses',
								currentUser,
								allMultiLingualData
							)}
						</em>
					</div>

					<div>
						<h6 className="bs-form-label-eligibility">Default Behavior:</h6>
					</div>

					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="defaultToNeedsReview"
								checked={eligibilityChecks?.defaultToNeedsReview || false}
								onChange={handleEligibilityCheckOnChange}
								disabled={!isSuperAdmin}
							/>
							<label className="bs-label" htmlFor="defaultToNeedsReview">
								<strong>Require Manual Review</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						Default all potentially "Eligible" bonuses to "Review" status.
					</div>

					<div>
						<h6 className="bs-form-label-eligibility">
							Inactive Employee/New Hire Cancellations:
						</h6>
					</div>

					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="referralBonusEmployee"
								checked={
									automationSettings?.employeeInactiveIneligibility || false
								}
								disabled={!isSuperAdmin}
								onChange={(e) => {
									const isChecked = e.target.checked;
									handleAutomationSettings(
										isChecked,
										'employeeInactiveIneligibility'
									);
								}}
							/>
							<label className="bs-label" htmlFor="referralBonusEmployee">
								<strong>
									Waiting Period - Active Employee Check (Bonus Cancelation)
								</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If the employee's account is disabled at any time during the bonus
						waiting period, the employee bonus will be canceled.
					</div>

					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="referralBonusCandidate"
								checked={
									automationSettings?.candidateInactiveIneligibility || false
								}
								disabled={!isSuperAdmin}
								onChange={(e) => {
									const isChecked = e.target.checked;
									handleAutomationSettings(
										isChecked,
										'candidateInactiveIneligibility'
									);
								}}
							/>
							<label className="bs-label" htmlFor="referralBonusCandidate">
								<strong>
									Waiting Period - New Hire Check (Bonus Cancelation)
								</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If the hired candidate is no longer active before the bonus waiting
						period is complete, the employee and candidate bonus will be
						canceled.
					</div>
					<div>
						<h6 className="bs-form-label-eligibility">
							Candidate Eligibility:
						</h6>
					</div>
					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="CheckForIneligibleCustomStatuses"
								disabled={!isSuperAdmin}
								checked={
									eligibilityChecks?.CheckForIneligibleCustomStatuses || false
								}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label
								className="bs-label"
								htmlFor="CheckForIneligibleCustomStatuses"
							>
								<strong>Ineligible Candidate Application Statuses</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						Candidates that have active applications with the following statuses
						will update the referral to be Ineligible.
					</div>
					{eligibilityChecks?.CheckForIneligibleCustomStatuses && (
						<div className="bs-form">
							<div className="bs-form-grp-eligibility">
								<div className="custom-form-group">
									<label className="bs-label-stage">
										Invalid Custom Statuses:
									</label>
									<Input
										className="custom-input"
										placeholder="Enter Status"
										id="IneligibleCustomStatuses"
										name="string"
										disabled={!isSuperAdmin}
										key="ineligible-custom-statuses-input"
										onChange={handleNewEligibilityCustomValidator}
									/>
								</div>
								<Button
									type="link"
									className="add-btn"
									size="small"
									disabled={!isSuperAdmin}
									onClick={addCustomValueFieldName}
								>
									<span className="icon-circle">
										<i className="icon-plus" />
									</span>
									Add Custom Status
								</Button>
							</div>
							<div className="bs-form-grp">
								<EligibilityCustomItem
									company={company}
									eligbilityChecks={eligibilityChecks}
									fieldName="IneligibleCustomStatuses"
									onUpdateCompany={onUpdateCompany}
									isSuperAdmin={isSuperAdmin}
									onCancelChanges={(originalEligibilityChecks) => {
										updateState({
											eligibilityChecks: originalEligibilityChecks,
										});
									}}
									onUpdateState={(updatedEligibilityChecks) => {
										updateState({
											eligibilityChecks: updatedEligibilityChecks,
										});
									}}
								/>
							</div>
						</div>
					)}
					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="CheckCandidateDomain"
								disabled={!isSuperAdmin}
								checked={eligibilityChecks?.CheckCandidateDomain || false}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label className="bs-label" htmlFor="CheckCandidateDomain">
								<strong>Ineligible Candidate Domains</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If a referred candidate has the following domain in their email
						address their referral will be updated to be Ineligible. Must
						include “@” before the domain.
					</div>
					{eligibilityChecks?.CheckCandidateDomain && (
						<div className="bs-form">
							<div className="bs-form-grp-eligibility">
								<div className="custom-form-group">
									<label className="bs-label-stage">
										Invalid Domain (Include @):
									</label>
									<Input
										className="custom-input"
										placeholder="Enter Domain"
										id="InvalidDomains"
										name="string"
										disabled={!isSuperAdmin}
										key="invalid-domains-input"
										onChange={handleNewEligibilityCustomValidator}
									/>
								</div>
								<Button
									type="link"
									className="add-btn"
									size="small"
									disabled={!isSuperAdmin}
									onClick={addCustomValueFieldName}
								>
									<span className="icon-circle">
										<i className="icon-plus" />
									</span>
									Add Invalid Domain
								</Button>
							</div>
							<div className="bs-form-grp">
								<EligibilityCustomItem
									company={company}
									eligbilityChecks={eligibilityChecks}
									fieldName="InvalidDomains"
									onUpdateCompany={onUpdateCompany}
									isSuperAdmin={isSuperAdmin}
									onCancelChanges={(originalEligibilityChecks) => {
										updateState({
											eligibilityChecks: originalEligibilityChecks,
										});
									}}
									onUpdateState={(updatedEligibilityChecks) => {
										updateState({
											eligibilityChecks: updatedEligibilityChecks,
										});
									}}
								/>
							</div>
						</div>
					)}

					<div>
						<h6 className="bs-form-label-eligibility">Employee Eligibility:</h6>
					</div>

					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="checkCurrentActiveEmployee"
								disabled={!isSuperAdmin}
								checked={eligibilityChecks?.checkCurrentActiveEmployee || false}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label className="bs-label" htmlFor="checkCurrentActiveEmployee">
								<strong>Active Employee Check (Referral Ineligible)</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If the employee's account is disabled at any time during the
						referral process, the referral will become ineligible:
					</div>
					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="CheckCandidateInternalApplicant"
								disabled={!isSuperAdmin}
								checked={
									eligibilityChecks?.CheckCandidateInternalApplicant || false
								}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label
								className="bs-label"
								htmlFor="CheckCandidateInternalApplicant"
							>
								<strong>Existing Employee Referral Check</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If the referred candidate is an existing employee, the referral will
						be updated to be Ineligible and Bonuses will be updated to be
						Ineligible. Requires an internal indicator on the candidate feed.
					</div>
					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="CheckClaimUserTitle"
								disabled={!isSuperAdmin}
								checked={eligibilityChecks?.CheckClaimUserTitle || false}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label className="bs-label" htmlFor="CheckClaimUserTitle">
								<strong>Ineligible Employee Titles</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						Employees who have these keywords in their titles are not eligible
						for bonuses. Referrals will be updated to be Ineligible.
					</div>
					{eligibilityChecks?.CheckClaimUserTitle && (
						<div className="bs-form">
							<div className="bs-form-grp-eligibility">
								<div className="custom-form-group">
									<label className="bs-label-stage">Invalid User Titles:</label>
									<Input
										className="custom-input"
										placeholder="Enter Title"
										id="CheckClaimUserTitleValuesToCheck"
										name="string"
										disabled={!isSuperAdmin}
										key="user-title-input"
										onChange={handleNewEligibilityCustomValidator}
									/>
								</div>
								<Button
									type="link"
									className="add-btn"
									size="small"
									disabled={!isSuperAdmin}
									onClick={addCustomValueFieldName}
								>
									<span className="icon-circle">
										<i className="icon-plus" />
									</span>
									Add Invalid Title
								</Button>
							</div>
							<div className="bs-form-grp">
								<EligibilityCustomItem
									company={company}
									eligbilityChecks={eligibilityChecks}
									fieldName="CheckClaimUserTitleValuesToCheck"
									onUpdateCompany={onUpdateCompany}
									isSuperAdmin={isSuperAdmin}
									onCancelChanges={(originalEligibilityChecks) => {
										updateState({
											eligibilityChecks: originalEligibilityChecks,
										});
									}}
									onUpdateState={(updatedEligibilityChecks) => {
										updateState({
											eligibilityChecks: updatedEligibilityChecks,
										});
									}}
								/>
							</div>
						</div>
					)}
					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="CheckCustomValues"
								disabled={!isSuperAdmin}
								checked={eligibilityChecks?.CheckCustomValues || false}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label className="bs-label-stage" htmlFor="CheckCustomValues">
								<strong>Custom User Values</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If a user matches these values, the referral will be updated to be
						Ineligible or Eligible based on the rules set for each custom value.
					</div>
					{eligibilityChecks?.CheckCustomValues && (
						<div className="bs-form">
							<div className="bs-form-grp-eligibility">
								<div className="custom-form-group">
									<input
										type="checkbox"
										id="StagesToCheckCustomValues-created"
										disabled={!isSuperAdmin}
										checked={
											eligibilityChecks?.StagesToCheckCustomValues?.includes(
												'created'
											) || false
										}
										onChange={handleEligibilityGranularityCheckOnChange}
									/>
									<label
										className="bs-label-stage"
										htmlFor="StagesToCheckCustomValues-created"
									>
										New Referral
									</label>
									<input
										type="checkbox"
										id="StagesToCheckCustomValues-accepted"
										disabled={!isSuperAdmin}
										checked={
											eligibilityChecks.StagesToCheckCustomValues?.includes(
												'accepted'
											) || false
										}
										onChange={handleEligibilityGranularityCheckOnChange}
									/>
									<label
										className="bs-label-stage"
										htmlFor="StagesToCheckCustomValues-accepted"
									>
										Accepted Referral
									</label>
									<input
										type="checkbox"
										id="StagesToCheckCustomValues-interviewing"
										disabled={!isSuperAdmin}
										checked={
											eligibilityChecks.StagesToCheckCustomValues?.includes(
												'interviewing'
											) || false
										}
										onChange={handleEligibilityGranularityCheckOnChange}
									/>
									<label
										className="bs-label-stage"
										htmlFor="StagesToCheckCustomValues-interviewing"
									>
										Application Created
									</label>
									<input
										type="checkbox"
										id="StagesToCheckCustomValues-hired"
										disabled={!isSuperAdmin}
										checked={
											eligibilityChecks.StagesToCheckCustomValues?.includes(
												'hired'
											) || false
										}
										onChange={handleEligibilityGranularityCheckOnChange}
									/>
									<label
										className="bs-label-stage"
										htmlFor="StagesToCheckCustomValues-hired"
									>
										Referral Hired
									</label>
								</div>
							</div>
							<div className="eligibility-explanation">
								Stages to Run Each Check
							</div>
							<div className="bs-form-grp-eligibility">
								<div className="custom-form-group">
									<label className="bs-label-stage">
										Custom Value Field Names:
									</label>
									<Input
										className="custom-input"
										placeholder="Enter Field Name"
										id="CustomValuesToCheck"
										name="object"
										disabled={!isSuperAdmin}
										defaultValue={state.customFieldNameValue?.value || ''}
										onChange={handleNewEligibilityCustomValidator}
									/>
								</div>
								<Button
									type="link"
									className="add-btn"
									size="small"
									disabled={!isSuperAdmin}
									onClick={addCustomValueFieldName}
								>
									<span className="icon-circle">
										<i className="icon-plus" />
									</span>
									Add Custom Field
								</Button>
							</div>
							<div className="bs-form-grp">
								<EligibilityCustomItem
									company={company}
									eligbilityChecks={eligibilityChecks}
									fieldName="CustomValuesToCheck"
									onUpdateCompany={onUpdateCompany}
									isSuperAdmin={isSuperAdmin}
									onCancelChanges={(originalEligibilityChecks) => {
										updateState({
											eligibilityChecks: originalEligibilityChecks,
										});
									}}
									onUpdateState={(updatedEligibilityChecks) => {
										updateState({
											eligibilityChecks: updatedEligibilityChecks,
										});
									}}
								/>
							</div>
							<div className="custom-form-group">
								<label className="custom-label">Select Custom Field: </label>
								<Select
									showSearch
									className="custom-input"
									placeholder="None"
									disabled={!isSuperAdmin}
									onSelect={handleFilterCustomValue}
								>
									{(eligibilityChecks?.CustomValuesToCheck || []).map(
										(custom) => {
											return (
												<Option key={custom.name} value={custom.name}>
													{custom.name}
												</Option>
											);
										}
									)}
								</Select>
							</div>
							{selectedCustom && (
								<>
									<div className="custom-label">
										{selectedCustom?.name || ''}
									</div>
									<>
										<div className="custom-form-group">
											<div className="bs-form-grp">
												<div className="custom-form-group">
													<label className="bs-label">
														Enter Invalid Values:
													</label>
													<Input
														className="custom-input"
														placeholder="Enter Value"
														key="invalid-values-input"
														onChange={
															handleNewEligibilityCustomValueVluesValidator
														}
													/>
												</div>
												<Button
													type="link"
													className="add-btn"
													size="small"
													disabled={!isSuperAdmin}
													onClick={handleCreateCustomValueInvalid}
												>
													<span className="icon-circle">
														<i className="icon-plus" />
													</span>
													Add Invalid
												</Button>
											</div>
										</div>
										<div className="bs-form-grp">
											<EligibilityCustomValueObject
												company={company}
												eligbilityChecks={eligibilityChecks}
												fieldObject={selectedCustom}
												fieldType="invalidValues"
												onUpdateCompany={onUpdateCompany}
												isSuperAdmin={isSuperAdmin}
												onCancelChanges={(
													originalEligibilityChecks,
													originalFieldObject
												) => {
													updateState({
														eligibilityChecks: originalEligibilityChecks,
														selectedCustom: originalFieldObject,
													});
												}}
												onUpdateState={(
													updatedEligibilityChecks,
													updatedFieldObject
												) => {
													updateState({
														eligibilityChecks: updatedEligibilityChecks,
														selectedCustom: updatedFieldObject,
													});
												}}
											/>
										</div>
										<div className="custom-form-group">
											<div className="bs-form-grp">
												<div className="custom-form-group">
													<label className="bs-label">
														Enter Valid Values:
													</label>
													<Input
														className="custom-input"
														placeholder="Enter Value"
														disabled={!isSuperAdmin}
														key="valid-values-input"
														onChange={
															handleNewEligibilityCustomValueVluesValidator
														}
													/>
												</div>
												<Button
													type="link"
													className="add-btn"
													size="small"
													disabled={!isSuperAdmin}
													onClick={handleCreateCustomValueValid}
												>
													<span className="icon-circle">
														<i className="icon-plus" />
													</span>
													Add Valid
												</Button>
											</div>
										</div>
									</>
									<div className="bs-form-grp">
										<EligibilityCustomValueObject
											company={company}
											eligbilityChecks={eligibilityChecks}
											fieldObject={selectedCustom}
											fieldType="validValues"
											onUpdateCompany={onUpdateCompany}
											isSuperAdmin={isSuperAdmin}
											onCancelChanges={(
												originalEligibilityChecks,
												originalFieldObject
											) => {
												updateState({
													eligibilityChecks: originalEligibilityChecks,
													selectedCustom: originalFieldObject,
												});
											}}
											onUpdateState={(
												updatedEligibilityChecks,
												updatedFieldObject
											) => {
												updateState({
													eligibilityChecks: updatedEligibilityChecks,
													selectedCustom: updatedFieldObject,
												});
											}}
										/>
									</div>
								</>
							)}
						</div>
					)}
					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="CheckCandidateLastHiredDate"
								disabled={!isSuperAdmin}
								checked={
									eligibilityChecks?.CheckCandidateLastHiredDate || false
								}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label className="bs-label" htmlFor="CheckCandidateLastHiredDate">
								<strong>Previously Employed Check</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If the candidate was previously employed within the timeframe set
						below (days), the referral will be updated to be Ineligible.
						Requires last hired date on candidate feed.
					</div>
					{eligibilityChecks?.CheckCandidateLastHiredDate && (
						<div className="bs-form">
							<div className="bs-form-grp-eligibility">
								<div className="custom-form-group">
									<label className="bs-label-stage">
										Enter allowable number of days since last hired:
									</label>
									<Input
										className="custom-input"
										placeholder="Enter Max Days"
										disabled={!isSuperAdmin}
										type="text"
										value={
											pendingInputValues[`eligibility-LastHiredMaxDays`] !==
											undefined
												? pendingInputValues[`eligibility-LastHiredMaxDays`]
												: eligibilityChecks?.LastHiredMaxDays || ''
										}
										id="LastHiredMaxDays"
										onChange={handleEligibilityInputChange}
									/>
								</div>
							</div>
						</div>
					)}
					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="CheckHiringManagerIds"
								checked={eligibilityChecks?.CheckHiringManagerIds || false}
								disabled={!isSuperAdmin}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label className="bs-label" htmlFor="CheckHiringManagerIds">
								<strong>Hiring Manager Check</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If the hiring manager is the referring user the referral will be
						updated to be ineligible for a bonus. Requires Hiring Manager
						Employee ID on Job Feed.
					</div>
					{eligibilityChecks?.CheckHiringManagerIds && (
						<div className="bs-form">
							<div className="bs-form-grp-eligibility">
								<div className="custom-form-group">
									<input
										type="checkbox"
										id="StagesToCheckHiringManagers-created"
										checked={
											eligibilityChecks?.StagesToCheckHiringManagers?.includes(
												'created'
											) || false
										}
										disabled={!isSuperAdmin}
										onChange={handleEligibilityGranularityCheckOnChange}
									/>
									<label
										className="bs-label-stage"
										htmlFor="StagesToCheckHiringManagers-created"
									>
										New Referral
									</label>
									<input
										type="checkbox"
										id="StagesToCheckHiringManagers-accepted"
										checked={
											eligibilityChecks?.StagesToCheckHiringManagers?.includes(
												'accepted'
											) || false
										}
										disabled={!isSuperAdmin}
										onChange={handleEligibilityGranularityCheckOnChange}
									/>
									<label
										className="bs-label-stage"
										htmlFor="StagesToCheckHiringManagers-accepted"
									>
										Accepted Referral
									</label>
									<input
										type="checkbox"
										id="StagesToCheckHiringManagers-interviewing"
										checked={
											eligibilityChecks?.StagesToCheckHiringManagers?.includes(
												'interviewing'
											) || false
										}
										disabled={!isSuperAdmin}
										onChange={handleEligibilityGranularityCheckOnChange}
									/>
									<label
										className="bs-label-stage"
										htmlFor="StagesToCheckHiringManagers-interviewing"
									>
										Application Created
									</label>
									<input
										type="checkbox"
										id="StagesToCheckHiringManagers-hired"
										checked={
											eligibilityChecks?.StagesToCheckHiringManagers?.includes(
												'hired'
											) || false
										}
										disabled={!isSuperAdmin}
										onChange={handleEligibilityGranularityCheckOnChange}
									/>
									<label
										className="bs-label-stage"
										htmlFor="StagesToCheckHiringManagers-hired"
									>
										Referral Hired
									</label>
								</div>
							</div>
						</div>
					)}
					<div className="eligibility-explanation">
						Stages to Run Each Check
					</div>
					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="CheckCandidateHiringManagerVsReferringEmployee"
								checked={
									eligibilityChecks?.CheckCandidateHiringManagerVsReferringEmployee ||
									false
								}
								disabled={!isSuperAdmin}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label
								className="bs-label"
								htmlFor="CheckCandidateHiringManagerVsReferringEmployee"
							>
								<strong>Hiring Manager Verification (Job vs AEF)</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If the hiring manager on the job is the hiring manager listed for
						the employee in the active employee feed, then the bonus will be
						Ineligible. Requires Manager ID on AEF.
					</div>
					<div>
						<h6 className="bs-form-label-eligibility">
							Referral Ownership and Grace Periods:
						</h6>
					</div>
					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="referralBonusOwnership"
								checked={ownershipSettings?.enabled || false}
								disabled={!isSuperAdmin}
								onChange={(e) =>
									handleOwnershipSettings(e.target.checked, 'enabled')
								}
							/>
							<label className="bs-label" htmlFor="referralBonusOwnership">
								<strong>Ownership Period for Referral Ownership</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If a candidate has been referred previously by an employee and is
						now being referred by another employee, the original referrer will
						receive credit for the candidate if it falls within the ownership
						period. Otherwise, there is no ownership period, and the last
						referrer will receive credit for the candidate.
					</div>

					{ownershipSettings?.enabled && (
						<div className="bs-form-grp-eligibility">
							<div className="custom-form-group">
								<label className="bs-label-stage">
									Ownership Period (Days):
								</label>
								<InputNumber
									className="custom-input"
									value={
										pendingInputValues.ownershipDays !== undefined
											? pendingInputValues.ownershipDays
											: get(ownershipSettings, 'days', 0)
									}
									disabled={!isSuperAdmin}
									style={{ width: 100 }}
									placeholder="Days"
									onChange={(value) => {
										updateState({
											pendingInputValues: {
												...pendingInputValues,
												ownershipDays: value,
											},
										});
										handleOwnershipSettingsDebouncedConfirm(value);
									}}
								/>
							</div>
						</div>
					)}
					{ownershipSettings?.enabled && (
						<div>
							<div className="bs-form-grp-eligibility">
								<div className="custom-form-group">
									<input
										type="checkbox"
										id="referralBonusPeriod"
										checked={ownershipSettings?.rollingOwnershipDates || false}
										disabled={!isSuperAdmin}
										onChange={(e) =>
											handleOwnershipSettings(
												e.target.checked,
												'rollingOwnershipDates'
											)
										}
									/>
									<label className="bs-label" htmlFor="referralBonusPeriod">
										<strong>Referral Ownership Refresh</strong>
									</label>
								</div>
							</div>
							<div className="eligibility-explanation">
								The ownership period can be retained by the original referrer if
								the candidate is referred again within the ownership period:{' '}
							</div>
						</div>
					)}

					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="CheckCandidateGraceDays"
								checked={eligibilityChecks?.CheckCandidateGraceDays || false}
								disabled={!isSuperAdmin}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label className="bs-label" htmlFor="CheckCandidateGraceDays">
								<strong>Grace Period for Applications Before Referrals</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If the candidate has applied for the job before the referral was
						made, it will be allowed as long as it falls within the grace period
						window below. Otherwise, it will be updated to be Ineligible.
					</div>

					{eligibilityChecks?.CheckCandidateGraceDays && (
						<div className="bs-form">
							<div className="bs-form-grp-eligibility">
								<div className="custom-form-group">
									<label className="bs-label-stage">Grace Period (Days):</label>
									<Input
										className="custom-input"
										placeholder="Enter Number of Grace Days"
										type="text"
										disabled={!isSuperAdmin}
										value={
											pendingInputValues[`eligibility-NumberOfGraceDays`] !==
											undefined
												? pendingInputValues[`eligibility-NumberOfGraceDays`]
												: eligibilityChecks.NumberOfGraceDays || ''
										}
										id="NumberOfGraceDays"
										onChange={handleEligibilityInputChange}
									/>
								</div>
							</div>
						</div>
					)}
					<div>
						<h6 className="bs-form-label-eligibility">
							Missing Data Safeguards:
						</h6>
					</div>
					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="EnableNeedsReview"
								disabled={!isSuperAdmin}
								checked={eligibilityChecks?.EnableNeedsReview || false}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label className="bs-label" htmlFor="EnableNeedsReview">
								<strong>Missing Data Fail-Safe</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						When data for enabled checks is missing, set bonus status to
						'Review'.
					</div>
					<div className="bs-form-grp-eligibility">
						<div className="custom-form-group">
							<input
								type="checkbox"
								id="CheckCandidateActive"
								disabled={!isSuperAdmin}
								checked={eligibilityChecks?.CheckCandidateActive || false}
								onChange={handleEligibilityCheckOnChange}
							/>
							<label className="bs-label" htmlFor="CheckCandidateActive">
								<strong>Missing Candidate ID Fail-Safe</strong>
							</label>
						</div>
					</div>
					<div className="eligibility-explanation">
						If a candidate never shows in active employee feed and cannot be
						verified, bonus status will be set to “Review” rather than being
						canceled. Requires matching Candidate ID on Active Employee Feed.
					</div>
				</div>
			</div>
		);
	};

	const renderStageInputs = () => {
		const { stages } = state;

		const handleAddStage = useCallback(
			(index) => {
				const nextStage = {};
				nextStage['stage' + index] = '';
				updateState({
					stages: [...stages, nextStage],
				});
			},
			[stages]
		);

		const handleRemoveStageClick = useCallback(
			(itemIndex) => {
				const copy = [...stages];
				let newValue = copy[itemIndex + 1];
				if (newValue) {
					copy.splice(itemIndex, 1);
					for (let i = itemIndex + 1; i <= copy.length; i++) {
						const nextStage = {};
						nextStage[`stage${i}`] = newValue[`stage${i + 1}`];
						copy[i - 1] = nextStage;
						newValue = copy[i];
					}
				} else {
					copy.splice(itemIndex, 1);
				}

				updateState({ stages: copy });
			},
			[stages]
		);

		if (stages.length > 0) {
			return stages.map((stage, index) => {
				return (
					<div key={index}>
						<div className="bs-form-grp">
							<div className="custom-form-group">
								<label className="bs-label bs-label-nobold bs-label-width">
									{`Custom Stage ${index + 1}`}:
								</label>
								<Form.Item
									name={`stage-${index + 1}`}
									initialValue={stage[`stage${index + 1}`]}
								>
									<Input
										className="custom-input"
										placeholder="Enter new stage"
									/>
								</Form.Item>
							</div>
							{index >= 0 && (
								<Button
									type="link"
									className="btn-link danger"
									onClick={() => handleRemoveStageClick(index)}
								>
									<i className="icon-bin" />
									Delete this stage
								</Button>
							)}
						</div>
						<div className="mb-4">
							{index === stages.length - 1 && index < 5 && (
								<Button
									type="link"
									className="add-btn"
									size="small"
									onClick={() => handleAddStage(index + 1)}
								>
									<span className="icon-circle">
										<i className="icon-plus" />
									</span>
									Add Stage
								</Button>
							)}
						</div>
					</div>
				);
			});
		}

		return (
			<div className="mb-4">
				<Button
					type="link"
					className="add-btn"
					size="small"
					onClick={() => handleAddStage(1)}
				>
					<span className="icon-circle">
						<i className="icon-plus" />
					</span>
					Add Stage
				</Button>
			</div>
		);
	};

	const renderReferralStatusMappings = () => {
		const currentMapping = get(company, 'referralCustomStatuses')
			? {
					...REFERRAL_STATUS,
					...parse(get(company, 'referralCustomStatuses')),
				}
			: { ...REFERRAL_STATUS };

		return (
			<>
				{Object.entries(currentMapping).map((entry) => {
					const key = entry[0];
					const value = entry[1];
					return (
						<div key={key} className="bs-form-grp">
							<div className="custom-form-group">
								<label className="bs-label bs-label-nobold bs-label-width">
									{REFERRAL_STATUS[key]}:
								</label>
								<Form.Item
									name={key}
									initialValue={value}
									rules={[
										{
											required: true,
											message: 'Please input referral status.',
										},
										{
											max: 15,
											message:
												'Referral status can not be more than 15 characters.',
										},
									]}
								>
									<Input className="custom-input" />
								</Form.Item>
							</div>
						</div>
					);
				})}
			</>
		);
	};

	const renderReferralMiddleStage = () => {
		const middleStage = get(company, 'referralStatus', 'Interviewing');

		return (
			<div className={FlexContainer}>
				<div className={BonusLabel} style={{ paddingBottom: 10 }}>
					Customize the default middle stage and add custom stages between
					"Accepted" and "Hired".
				</div>
				<div className={SettingStyle.groupRow}>
					<div
						className={SettingStyle.stageLabel}
						style={{
							color: 'black',
							paddingBottom: 10,
							width: 190,
						}}
					>
						Default Middle Stage:
					</div>
					<Form.Item
						name="referralMiddleStage"
						initialValue={middleStage}
						rules={[
							{
								required: true,
								message: 'Please input referral status.',
							},
						]}
					>
						<Input className={SettingStyle.StageInputStyles} />
					</Form.Item>
				</div>
			</div>
		);
	};

	const renderBonusReporting = () => {
		const {
			deliveryFrequency,
			monthlyFrequency,
			weeklyFrequency,
			bonusReportSetting,
			selectedDeliveryDateOption,
			bonusEmailAddress,
		} = state;

		const handleBonusReportStatusOnChange = useCallback(() => {
			let updatedSettings = {
				isEnable:
					bonusReportSetting && bonusReportSetting.isEnable
						? !bonusReportSetting.isEnable
						: true,
				frequency:
					bonusReportSetting && bonusReportSetting.frequency
						? bonusReportSetting.frequency
						: 'Monthly',
				time:
					bonusReportSetting && bonusReportSetting.time
						? bonusReportSetting.time
						: '1',
				emailAddresses:
					bonusReportSetting && bonusReportSetting.emailAddresses
						? bonusReportSetting.emailAddresses
						: '',
			};

			updateState({ bonusReportSetting: updatedSettings });

			onUpdateCompany({
				input: {
					id: state?.company?.id || companyId,
					bonusReportSetting: JSON.stringify(updatedSettings),
				},
			});
		}, [bonusReportSetting, state?.company?.id, onUpdateCompany]);

		const handleBonusTime = useCallback(
			(value) => {
				const updatedSettings = { ...bonusReportSetting, time: value };
				updateState({ bonusReportSetting: updatedSettings });

				onUpdateCompany({
					input: {
						id: state?.company?.id || companyId,
						bonusReportSetting: JSON.stringify(updatedSettings),
					},
				});
			},
			[bonusReportSetting, state?.company?.id, onUpdateCompany]
		);

		const addEmailToBonusReport = useCallback(() => {
			const { company } = state;
			const emailAddresses =
				bonusReportSetting && bonusReportSetting.emailAddresses
					? bonusReportSetting.emailAddresses.split(',')
					: [];

			if (bonusEmailAddress && !emailAddresses.includes(bonusEmailAddress)) {
				emailAddresses.push(bonusEmailAddress);
				const updatedSettings = {
					...bonusReportSetting,
					emailAddresses: emailAddresses.join(','),
				};

				updateState({
					bonusReportSetting: updatedSettings,
					bonusEmailAddress: '',
				});

				onUpdateCompany({
					input: {
						id: company?.id || companyId,
						bonusReportSetting: JSON.stringify(updatedSettings),
					},
				});
			}
		}, [
			bonusReportSetting,
			bonusEmailAddress,
			state?.company,
			onUpdateCompany,
		]);

		const handleNewBonusCustomValidator = useCallback((event) => {
			let value = event.target.value ? event.target.value.toLowerCase() : '';
			value &&= sanitize(value);
			updateState({ bonusEmailAddress: value });
		}, []);

		const options = deliveryFrequency.map((x) => (
			<Option key={x.val} value={x.val}>
				{x.val}
			</Option>
		));

		const monthlyFrequencyOptions = monthlyFrequency.map((w) => (
			<Option key={w.key} value={w.val}>
				{w.key}
			</Option>
		));

		const weeklyFrequencyOptions = weeklyFrequency.map((w) => (
			<Option key={w.key} value={w.value}>
				{w.key}
			</Option>
		));

		return (
			<div className="bs-form">
				<h6 className="bs-form-label">Automated Bonus Eligible Reports</h6>
				<div className="bs-form-grp">
					<div className="custom-form-group custom-checkbox">
						<input
							type="checkbox"
							id="referralBonusEligibleReports"
							checked={
								(bonusReportSetting && bonusReportSetting.isEnable) || false
							}
							onChange={handleBonusReportStatusOnChange}
						/>
						<label className="bs-label" htmlFor="referralBonusEligibleReports">
							Enable Automated Bonus Report Export:
						</label>
					</div>
				</div>
				{bonusReportSetting && bonusReportSetting.isEnable && (
					<div className="bs-form">
						<div className="bs-form-grp">
							<div className="custom-form-group">
								<label className="bs-label">
									Send Automated Bonus Report to:
								</label>
								<Input
									className="custom-input"
									placeholder="Enter Email"
									value={bonusEmailAddress || ''}
									key="bonus-email-input"
									onChange={handleNewBonusCustomValidator}
								/>
							</div>
							<Button
								type="link"
								className="add-btn"
								size="small"
								onClick={addEmailToBonusReport}
							>
								<span className="icon-circle">
									<i className="icon-plus" />
								</span>
								Add Email
							</Button>
						</div>
						<div className="bs-form-grp">
							<BonusReportRecipientItem
								company={state.company}
								bonusReportSetting={bonusReportSetting}
								onUpdateCompany={onUpdateCompany}
							/>
						</div>

						<div className="bs-form-grp">
							<div className="custom-form-group">
								<label className="bs-label">Delivery Frequency</label>
								<Select
									placeholder="Select Frequency"
									style={{ width: 180 }}
									className="custom-input"
									disabled={
										bonusReportSetting &&
										bonusReportSetting.emailAddresses === ''
									}
									onSelect={handleBonusFrequency}
									value={bonusReportSetting?.frequency}
								>
									<Select.Option value="Weekly">Weekly</Select.Option>
									<Select.Option value="Monthly">Monthly</Select.Option>
									<Select.Option value="Daily">Daily</Select.Option>
								</Select>
							</div>
						</div>

						{selectedDeliveryDateOption && (
							<div className="bs-form-grp">
								<div className="custom-form-group">
									<label className="bs-label">Delivery date</label>
									<Select
										placeholder="Select"
										style={{ width: 180 }}
										className="custom-input"
										disabled={
											bonusReportSetting &&
											bonusReportSetting.emailAddresses === ''
										}
										onSelect={handleBonusTime}
										value={bonusReportSetting?.time}
									>
										{bonusReportSetting?.frequency === 'Weekly'
											? weeklyFrequencyOptions
											: monthlyFrequencyOptions}
									</Select>
								</div>
							</div>
						)}

						<label className="custom-label">
							Note: Report will be sent at 8:00 AM EST on the scheduled day.
							Only{' '}
							<strong>
								<em>Eligible</em>{' '}
							</strong>
							bonuses in the select time frame will be sent. If there are no
							bonuses available, a report will not be sent.
						</label>
					</div>
				)}
			</div>
		);
	};

	// Main render
	if (!get(currentUser, 'company')) {
		return <Spinner />;
	}

	return (
		<main>
			<div className="page-title">
				<h2 className="page-heading">Bonus Policies</h2>
				<ul className="info-action">
					<li>
						<Button
							type="link"
							className="add-btn btn-lg"
							onClick={handleAddNewBonusClick}
						>
							<span className="icon-circle">
								<i className="icon-plus" />
							</span>
							{ml('Add Bonus Tier', currentUser, allMultiLingualData)}
						</Button>
					</li>
				</ul>
			</div>

			{state.submitting ? <Spinner company={state.company} /> : renderList()}

			<h3 className="bonus-heading">
				{ml('Bonus Settings', currentUser, allMultiLingualData)}
			</h3>

			<Form form={mainForm}>
				<Collapse
					className="bonus-collapse"
					bordered={false}
					expandIconPosition="end"
				>
					<Collapse.Panel
						key="1"
						className="bonus-collapse-card"
						extra={
							<div className="bonus-collapse-icon-wrap">
								<i className="icon-arrow-down bonus-collapse-icon" />
							</div>
						}
						header={
							<h4>
								{ml(
									'Universal Referral Policy Automations',
									currentUser,
									allMultiLingualData
								)}
							</h4>
						}
					>
						{renderEligibilitySettings()}
					</Collapse.Panel>
				</Collapse>
			</Form>

			<GroupCurrency
				currentUser={currentUser}
				userGroups={userGroups}
				setCurrentUserGroup={setCurrentUserGroup}
				onUpdateUserGroup={onUpdateUserGroup}
			/>

			<GroupMeasurement
				allMultiLingualData={allMultiLingualData}
				currentUser={currentUser}
				userGroups={userGroups}
				setCurrentUserGroup={setCurrentUserGroup}
				onUpdateUserGroup={onUpdateUserGroup}
			/>

			<Form form={stagesForm} onFinish={handleSubmitStages}>
				<Collapse
					className="bonus-collapse"
					bordered={false}
					expandIconPosition="end"
				>
					<Collapse.Panel
						className="bonus-collapse-card"
						extra={
							<div className="bonus-collapse-icon-wrap">
								<i className="icon-arrow-down bonus-collapse-icon" />
							</div>
						}
						header={<h4>Referral Stages</h4>}
					>
						<div className="bs-form">
							<h6 className="bs-form-label">
								Add custom stages between "Accepted" and "Hired".
							</h6>
							{renderStageInputs()}
							{renderReferralStatusMappings()}
							{renderReferralMiddleStage()}
							<Button type="primary" size="large" htmlType="submit">
								Save Referral Stages
							</Button>
						</div>
					</Collapse.Panel>
				</Collapse>
			</Form>

			<Collapse
				className="bonus-collapse"
				bordered={false}
				expandIconPosition="end"
			>
				<Collapse.Panel
					key="1"
					className="bonus-collapse-card"
					extra={
						<div className="bonus-collapse-icon-wrap">
							<i className="icon-arrow-down bonus-collapse-icon" />
						</div>
					}
					header={<h4>Bonus Reporting</h4>}
				>
					{renderBonusReporting()}
					{renderBonusNotification()}
				</Collapse.Panel>
			</Collapse>
		</main>
	);
};
